import { create } from 'zustand';

// import { persist } from 'zustand/middleware';
import type { Discount, Extra, Insurance, Reservation } from '@/api';
import type { Address, SubscriptionCar } from '@/api/subscriptions/types';

export interface Mileage {
  name: string;
  amount: number;
  costPerMile: number;
  unlimited?: boolean;
}

interface SubscriptionState {
  address: Address | null;
  startDatetime: Date | null;
  period: 'weekly' | 'monthly' | '6Months' | '';
  brandName: string;
  modelName: string;
  trimName: string;
  car: SubscriptionCar | null;
  autopilot: Extra | null;
  mileage: Mileage | null;
  mileageCost: number;
  insurance: Insurance | null;
  discount: Discount | null;
  signature: string | undefined;
  stripeSecret: string;
  ephemeralKey: string;
  customerSessionSecret: '';
  stripeCustomerId: string;
  signingSignature: boolean;
  setAddress: (address: any) => void;
  setStartDatetime: (startDatetime: Date) => void;
  setPeriod: (period: 'weekly' | 'monthly' | '6Months' | '') => void;
  setBrandName: (brandName: string) => void;
  setModelName: (modelName: string) => void;
  setTrimName: (trimName: string) => void;
  setCar: (car: SubscriptionCar) => void;
  setAutopilot: (autopilot: Extra) => void;
  setMileage: (mileage: Mileage) => void;
  setMileageCost: (mileageCost: number) => void;
  setInsurance: (insurance: Insurance) => void;
  setDiscount: (discount: Discount) => void;
  setSignature: (signature: string) => void;
  setReservation: (reservation: Reservation) => void;
  setStripeSecret: (stripeSecret: string) => void;
  setEphemeralKey: (ephemeralKey: string) => void;
  setCustomerSessionSecret: (customerSessionSecret: string) => void;
  setStripeCustomerId: (stripeCustomerId: string) => void;
  setSigningSignature: (signingSignature: boolean) => void;
}

// const useSubscriptionStore = create<SubscriptionState>()(
//   persist(
//     (set) => ({
//       address: '',
//       startDatetime: null,
//       period: 'monthly',
//       modelName: '',
//       trimName: '',
//       car: null,
//       autopilot: null,
//       mileage: 0,
//       insurance: null,
//       setAddress: (address: string) => {
//         set((state) => ({
//           ...state,
//           address,
//         }));
//       },
//       setStartDatetime: (startDatetime: Date) => {
//         set((state) => ({
//           ...state,
//           startDatetime,
//         }));
//       },
//       setPeriod: (period: 'weekly' | 'monthly' | '6Months') => {
//         set((state) => ({
//           ...state,
//           period,
//         }));
//       },
//       setModelName: (modelName: string) => {
//         set((state) => ({
//           ...state,
//           modelName,
//         }));
//       },
//       setTrimName: (trimName: string) => {
//         set((state) => ({
//           ...state,
//           trimName,
//         }));
//       },
//       setCar: (car: SubscriptionCar) => {
//         set((state) => ({
//           ...state,
//           car,
//         }));
//       },
//       setAutopilot: (autopilot: Extra) => {
//         set((state) => ({
//           ...state,
//           autopilot,
//         }));
//       },
//       setMileage: (mileage: number) => {
//         set((state) => ({
//           ...state,
//           mileage,
//         }));
//       },
//       setInsurance: (insurance: Insurance) => {
//         set((state) => ({
//           ...state,
//           insurance,
//         }));
//       },
//     }),
//     {
//       name: 'subscription-storage', // unique name for the storage
//       getStorage: () => localStorage, // specify the storage (localStorage in this case)
//     }
//   )
// );

const useSubscriptionStore = create<SubscriptionState>((set) => ({
  address: null,
  startDatetime: null,
  period: '',
  modelName: '',
  trimName: '',
  car: null,
  autopilot: null,
  mileage: null,
  mileageCost: 0,
  insurance: null,
  discount: null,
  signature: '',
  stripeSecret: '',
  ephemeralKey: '',
  customerSessionSecret: '',
  stripeCustomerId: '',
  signingSignature: false,
  setAddress: (address: any) => {
    set((state) => ({
      ...state,
      address,
    }));
  },
  setStartDatetime: (startDatetime: Date) => {
    set((state) => ({
      ...state,
      startDatetime,
    }));
  },
  setPeriod: (period: 'weekly' | 'monthly' | '6Months' | '') => {
    set((state) => ({
      ...state,
      period,
    }));
  },
  setBrandName: (brandName: string) => {
    set((state) => ({
      ...state,
      brandName,
    }));
  },
  setModelName: (modelName: string) => {
    set((state) => ({
      ...state,
      modelName,
    }));
  },
  setTrimName: (trimName: string) => {
    set((state) => ({
      ...state,
      trimName,
    }));
  },
  setCar: (car: SubscriptionCar) => {
    set((state) => ({
      ...state,
      car,
    }));
  },
  setAutopilot: (autopilot: Extra) => {
    set((state) => ({
      ...state,
      autopilot,
    }));
  },
  setMileage: (mileage: Mileage) => {
    set((state) => ({
      ...state,
      mileage,
    }));
  },
  setMileageCost: (mileageCost: number) => {
    set((state) => ({
      ...state,
      mileageCost,
    }));
  },
  setInsurance: (insurance: Insurance) => {
    set((state) => ({
      ...state,
      insurance,
    }));
  },
  setDiscount: (discount: Discount) => {
    set((state) => ({
      ...state,
      discount,
    }));
  },
  setSignature: (signature: string) => {
    set((state) => ({
      ...state,
      signature,
    }));
  },
  setStripeSecret: (stripeSecret: string) => {
    set((state) => ({
      ...state,
      stripeSecret,
    }));
  },
  setEphemeralKey: (ephemeralKey: string) => {
    set((state) => ({
      ...state,
      ephemeralKey,
    }));
  },
  setCustomerSessionSecret: (customerSessionSecret: string) => {
    set((state) => ({
      ...state,
      customerSessionSecret,
    }));
  },
  setStripeCustomerId: (stripeCustomerId: string) => {
    set((state) => ({
      ...state,
      stripeCustomerId,
    }));
  },
  setSigningSignature: (signingSignature: boolean) => {
    set((state) => ({
      ...state,
      signingSignature,
    }));
  },
}));

export default useSubscriptionStore;

// export const useSubscriptionStore = createSelectors(_useSubscriptionStore);

// export const setAddress = (address: string) =>
//   _useSubscriptionStore.getState().setAddress(address);
